import { purposeIds } from './config.ts';

type ConsentedPurposes = {
	_id: string;
	name: string;
};

type CustomVendorsResponse = {
	consentedPurposes: ConsentedPurposes[];
};

/**
 * This is a partial type definition for the consent data stored by SourcePoint in localStorage.
 * It only includes the fields we are interested in and should be extended as needed.
 */
type ConsentData = {
	customVendorsResponse: CustomVendorsResponse;
	euconsent?: string;
} | null;

export function getConsentDataFromStorage(propertyId: number): ConsentData {
	const userConsents = window.localStorage.getItem(`_sp_user_consent_${propertyId}`);

	if (userConsents === null) {
		return null;
	}

	try {
		const parsedData: ConsentData = JSON.parse(userConsents);
		return parsedData;
	} catch (error) {
		console.error(
			`Failed to parse consent data from localStorage for property ID ${propertyId}:`,
			error,
		);
		return null;
	}
}

/**
 * Factory function to create a consent reader for a specific SourcePoint property ID.
 * @param propertyId
 * @returns An object with methods to read consent data from localStorage.
 *
 * @example
 * ```typescript
 * import { createConsentReader } from '@derstandard/sourcepoint';
 *
 * const { getConsentedPurposes } = createConsentReader(12345);
 * const consentedPurposes = getConsentedPurposes();
 * ```
 */
export function createConsentReader(propertyId: number) {
	if (typeof propertyId !== 'number') {
		throw new TypeError(
			`The provided SourcePoint property ID is not a number, got ${typeof propertyId}`,
		);
	}

	return {
		/**
		 * Retrieves the list of consented purposes from the stored consent data.
		 * @returns
		 */
		getConsentedPurposes(): ConsentedPurposes[] {
			const storageData = getConsentDataFromStorage(propertyId);

			return storageData?.customVendorsResponse?.consentedPurposes ?? [];
		},
	};
}

/**
 * Helper function to check whether a user has given consent to personalized ads.
 * @param propertyId
 * @param purposeId
 * @returns a true when the user has given consent to personalized ads
 */
export function hasConsentForPurpose(
	propertyId: number,
	purpose: keyof typeof purposeIds,
	consentedPurposes?: ConsentedPurposes[],
): boolean {
	const purposeType = purposeIds[purpose];

	if (!purposeType) {
		return false;
	}

	if (!consentedPurposes) {
		const consentReader = createConsentReader(propertyId);
		consentedPurposes = consentReader.getConsentedPurposes();
	}

	const purposeListIds = consentedPurposes.map((item) => item._id);

	return Object.values(purposeType).some((id: string) => purposeListIds.includes(id));
}

/**
 * Helper function to check whether a user has given consent to personalized ads.
 * @param propertyId
 * @returns a true when the user has given consent to personalized ads
 *
 * @example
 * ```typescript
 * import { hasPersonalizedAdsConsent } from '@derstandard/sourcepoint';
 *
 * if (hasPersonalizedAdsConsent(12345)) {
 * 	// display personalized ads
 * }
 * ```
 *
 * @deprecated Use `hasConsentForPurpose` directly.
 */
export function hasPersonalizedAdsConsent(propertyId: number) {
	return hasConsentForPurpose(propertyId, 'personalizedAds');
}

/**
 * Helper function to check whether a user has given consent to third party content.
 * @param propertyId
 * @returns a true when the user has given consent to third party content
 *
 * @example
 * ```typescript
 * import { hasThirdPartyContentConsent } from '@derstandard/sourcepoint';
 *
 * if (hasThirdPartyContentConsent(12345)) {
 * 	// display third party embed
 * }
 * ```
 *
 * @deprecated Use `hasConsentForPurpose` directly.
 */
export function hasThirdPartyContentConsent(propertyId: number) {
	return hasConsentForPurpose(propertyId, 'thirdPartyContent');
}

/**
 * Helper function to check whether a user has given consent to web analytics and optimization.
 * @param propertyId
 * @returns a true when the user has given consent to web analytics and optimization
 *
 * @example
 * ```typescript
 * import { hasWebAnalyticsConsent } from '@derstandard/sourcepoint';
 *
 * if (hasWebAnalyticsConsent(12345)) {
 * 	// track the user's action
 * }
 * ```
 *
 * @deprecated Use `hasConsentForPurpose` directly.
 */
export function hasWebAnalyticsConsent(propertyId: number) {
	return hasConsentForPurpose(propertyId, 'webAnalytics');
}
